/**
 * DemoBrandHub — token-gated 12-brand investor demo launchpad.
 *
 * Route: /demo?key=<DEMO_PRESENTER_KEY>
 *
 * Server-side gating: /api/demo/verify-key. Wrong / missing key → 404.
 * On valid key, fetches /api/demo/brands and renders 3 tier groups:
 *   • Enterprise (Vector Pictures, Apex Beverages, Skyline Airways, Helix Motors)
 *   • Mid-market (Grandburger, TerraTire, MatchPoint, Pulse Fitness)
 *   • Local SMB (ShineWash, Brew & Co., Westside Auto, Aurora Labs)
 *
 * Click a card → POST /api/demo/login-as-brand → cookies set → /home.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Building2, Film, Wine, Plane, Car, Beef, Disc3, Heart,
  Activity, Droplets, Coffee, Sparkles, ArrowRight, Sparkle,
} from 'lucide-react';
import ProBadge from '../components/ui/ProBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  Building2, Film, Wine, Plane, Car, Beef, Disc3, Heart,
  Activity, Droplets, Coffee, Sparkles,
};

const TIER_META = {
  enterprise: { label: 'Enterprise', sub: 'Multi-SKU portfolios · $200K–$1M wallets' },
  mid:        { label: 'Mid-market', sub: 'DTC national brands · $50K–$250K wallets' },
  local:      { label: 'Local / SMB', sub: 'Hyperlocal · $5K–$25K wallets' },
};

export default function DemoBrandHub() {
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [state, setState] = useState('checking'); // checking | allow | deny

  useEffect(() => {
    if (!key) { setState('deny'); return; }
    axios.get(`${API}/api/demo/verify-key`, {
      params: { key }, validateStatus: () => true,
    }).then(({ status, data }) => {
      setState(status === 200 && data?.valid ? 'allow' : 'deny');
    }).catch(() => setState('deny'));
  }, [key]);

  if (state === 'checking') {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] flex items-center justify-center"
           data-testid="demo-checking">
        <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
      </div>
    );
  }
  if (state === 'deny') {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex items-center justify-center px-6"
           data-testid="demo-404">
        <div className="max-w-sm text-center space-y-3">
          <h1 className="text-7xl font-black tracking-tight text-gray-700">404</h1>
          <p className="text-sm text-gray-500">This page doesn&apos;t exist.</p>
          <Link to="/login" className="inline-block mt-4 text-xs text-gray-600 hover:text-gray-300">
            Return home
          </Link>
        </div>
      </div>
    );
  }
  return <DemoBody presenterKey={key} />;
}

function DemoBody({ presenterKey }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [launching, setLaunching] = useState(null); // brand_id being launched

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/demo/brands`, {
          params: { key: presenterKey },
        });
        if (dead) return;
        setBrands(data?.brands || []);
      } catch (e) {
        if (dead) return;
        setErr(e?.response?.data?.detail || e?.message || 'Failed to load demo brands.');
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [presenterKey]);

  const grouped = useMemo(() => {
    const out = { enterprise: [], mid: [], local: [] };
    for (const b of brands) {
      const tier = out[b.tier] ? b.tier : 'mid';
      out[tier].push(b);
    }
    return out;
  }, [brands]);

  const launchBrand = async (brand) => {
    setLaunching(brand.id);
    try {
      await axios.post(`${API}/api/demo/login-as-brand`, null, {
        params: { key: presenterKey, brand_id: brand.id },
        withCredentials: true,
      });
      // Hard redirect so AuthContext re-hydrates from the new cookies.
      window.location.replace('/home');
    } catch (e) {
      setLaunching(null);
      setErr(e?.response?.data?.detail || 'Launch failed.');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="demo-hub">
      {/* Ambient atmosphere — soft teal + pink corner glows for a more
          inviting feel than the flat black canvas. */}
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.10) 0%, transparent 65%)', filter: 'blur(8px)' }} />
      <div className="absolute -top-24 right-0 w-[420px] h-[420px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(255,77,148,0.10) 0%, transparent 65%)', filter: 'blur(10px)' }} />

      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 z-10
                         bg-[#0A0A0A]/85 backdrop-blur-xl relative">
        <div className="max-w-[1440px] mx-auto px-8 py-4 flex items-center gap-3">
          <Sparkle className="h-4 w-4 text-[#00D4FF]" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500">
              Wave · Demo Studio
            </div>
            <h1 className="text-base font-medium text-white">
              Pick a vertical to walk through
            </h1>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] text-gray-500">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Presenter session active
          </div>
        </div>
      </header>

      {loading && (
        <div className="max-w-7xl mx-auto px-6 py-24 flex items-center justify-center"
             data-testid="demo-loading">
          <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
        </div>
      )}

      {err && !loading && (
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="border border-red-500/20 bg-red-500/5 rounded-lg p-5 text-sm text-red-300"
               data-testid="demo-error">
            {err}
          </div>
        </div>
      )}

      {!loading && !err && brands.length === 0 && (
        <div className="max-w-7xl mx-auto px-6 py-24 text-center" data-testid="demo-empty">
          <p className="text-sm text-gray-400">
            No demo brands have been seeded against this database yet.
          </p>
          <p className="mt-2 text-xs text-gray-600">
            Run <code className="px-1.5 py-0.5 rounded bg-white/5 text-gray-400">
              POST /api/demo/setup-data?key=…
            </code> to seed.
          </p>
        </div>
      )}

      {!loading && !err && brands.length > 0 && (
        <main className="max-w-[1440px] mx-auto px-8 py-6 space-y-6 relative">
          {/* Hero strip — gives the demo a real "open the door" moment
              before the brand picker. */}
          <section className="rounded-2xl px-7 py-5 flex flex-col md:flex-row md:items-center gap-4"
                   style={{
                     background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(255,77,148,0.06) 100%)',
                     border: '1px solid rgba(255,255,255,0.06)',
                   }}>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#00D4FF] mb-1.5">
                Demo Studio · {brands.length} brands · 3 tiers
              </p>
              <h2 className="text-2xl font-black text-white leading-tight">
                Three brands. Three tiers. One closed loop.
              </h2>
              <p className="text-[12px] text-gray-400 mt-1.5 leading-relaxed max-w-xl">
                Each brand below is a complete sandbox — historical waves, live campaigns, an approval queue, and Digital DNA-matched cohorts wired through every page.
              </p>
            </div>
          </section>

          {/* One row, three brands — Enterprise · Mid-market · Local —
              left to right, all above the fold. The card itself surfaces
              the tier badge so we don't need separate tier section headers. */}
          <section data-testid="brand-row">
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
              data-testid="tier-row-all"
            >
              {(['enterprise', 'mid', 'local'])
                .flatMap(tier => grouped[tier])
                .map(brand => (
                  <BrandCard
                    key={brand.id}
                    brand={brand}
                    isLaunching={launching === brand.id}
                    anyLaunching={!!launching}
                    onLaunch={() => launchBrand(brand)}
                  />
                ))}
            </div>
          </section>

          {/* Hide horizontal scrollbar without losing scroll behaviour */}
          <style>{`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
        </main>
      )}
    </div>
  );
}

function BrandCard({ brand, isLaunching, anyLaunching, onLaunch }) {
  const Icon = ICONS[brand.icon] || Building2;
  return (
    <button
      type="button"
      onClick={onLaunch}
      disabled={anyLaunching}
      data-testid={`brand-card-${brand.id}`}
      className="group relative text-left rounded-xl overflow-hidden
                 border border-white/5 bg-white/[0.02] hover:bg-white/[0.04]
                 hover:border-white/10 transition
                 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {brand.hero_url && (
        <div className="aspect-[16/9] w-full overflow-hidden bg-white/5">
          <img
            src={brand.hero_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover opacity-60
                       group-hover:opacity-80 group-hover:scale-105
                       transition duration-500"
          />
        </div>
      )}
      <div className="p-4 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-md shrink-0"
              style={{ background: `${brand.color}1A`, color: brand.color }}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500 truncate">
              {brand.vertical}
            </span>
          </div>
          {brand.tier === 'enterprise' && <ProBadge tier="enterprise" size="sm" withIcon={false} />}
          {brand.tier === 'mid' && <ProBadge tier="pro" size="sm" withIcon={false} />}
        </div>
        <h3 className="text-base font-medium text-white leading-snug">
          {brand.brand_name}
        </h3>
        <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">
          {brand.tagline}
        </p>
        {brand.sample_campaign && (
          <p className="text-[11px] text-gray-600 leading-relaxed line-clamp-2 pt-1
                        border-t border-white/5">
            {brand.sample_campaign}
          </p>
        )}
        <div className="flex items-center gap-1.5 pt-1 text-[11px] font-medium"
             style={{ color: brand.color }}>
          {isLaunching ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Launching…
            </>
          ) : (
            <>
              Open workspace
              <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
            </>
          )}
        </div>
      </div>
    </button>
  );
}
